import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { StatsCard } from '../components/ui/stats-card';
import {
    FileText,
    Download,
    Search,
    RefreshCw,
    Settings,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Calendar,
    Filter,
    MoreHorizontal
} from 'lucide-react';
import { InvoiceAPI, Invoice, InvoiceStats } from '../lib/api';
import { Pagination } from '../components/ui/pagination';
import InvoiceConfigModal from '../components/invoices/InvoiceConfigModal';

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount);
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export default function InvoicesPage() {
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [stats, setStats] = useState<InvoiceStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'generated' | 'downloaded'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(20);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [regenerating, setRegenerating] = useState<string | null>(null);

    useEffect(() => {
        fetchInvoices();
        fetchStats();
    }, [currentPage, statusFilter]);

    const fetchInvoices = async () => {
        setLoading(true);
        setError(null);
        try {
            const params: any = {
                page: currentPage,
                limit: pageSize,
                sortBy: 'createdAt',
                sortOrder: 'desc'
            };
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            const response = await InvoiceAPI.getAll(params);
            if (response.success) {
                setInvoices(response.data.invoices);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await InvoiceAPI.getStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    };

    const handleRegenerate = async (orderId: string) => {
        setRegenerating(orderId);
        try {
            const response = await InvoiceAPI.regenerate(orderId);
            if (response.success) {
                fetchInvoices();
            }
        } catch (err) {
            console.error('Failed to regenerate invoice:', err);
        } finally {
            setRegenerating(null);
        }
    };

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const filteredInvoices = invoices.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6 px-1 sm:px-0">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Invoice Management</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        View and manage all generated invoices
                    </p>
                </div>
                <Button onClick={() => setConfigModalOpen(true)} variant="outline">
                    <Settings className="h-4 w-4 mr-2" />
                    Configure
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <StatsCard
                        title="Total Invoices"
                        value={stats.summary.totalInvoices}
                        icon={<FileText className="h-5 w-5" />}
                        iconClassName="bg-blue-50 text-blue-600"
                    />
                    <StatsCard
                        title="Total Amount"
                        value={formatCurrency(stats.summary.totalAmount)}
                        icon={<FileText className="h-5 w-5" />}
                        iconClassName="bg-green-50 text-green-600"
                    />
                    <StatsCard
                        title="Downloaded"
                        value={stats.summary.downloadedCount}
                        icon={<Download className="h-5 w-5" />}
                        iconClassName="bg-purple-50 text-purple-600"
                    />
                    <StatsCard
                        title="Generated Only"
                        value={stats.summary.generatedCount}
                        icon={<FileText className="h-5 w-5" />}
                        iconClassName="bg-yellow-50 text-yellow-600"
                    />
                    <StatsCard
                        title="Total Downloads"
                        value={stats.summary.totalDownloads}
                        icon={<Download className="h-5 w-5" />}
                        iconClassName="bg-orange-50 text-orange-600"
                    />
                </div>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full sm:w-auto">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search invoices..."
                                className="pl-10 w-full sm:w-[300px]"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-2">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Filter className="h-4 w-4 mr-2" />
                                        {statusFilter === 'all' ? 'All Status' : statusFilter}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                                        All Status
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setStatusFilter('generated')}>
                                        Generated
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setStatusFilter('downloaded')}>
                                        Downloaded
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="outline" size="sm" onClick={fetchInvoices}>
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Invoices Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Invoices</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {error && (
                        <div className="p-4 text-red-600 bg-red-50 rounded-md m-4">
                            {error}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Invoice #</TableHead>
                                    <TableHead>Order #</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Downloads</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : filteredInvoices.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                                            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                            <p>No invoices found</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredInvoices.map((invoice) => (
                                        <TableRow key={invoice._id}>
                                            <TableCell className="font-medium text-primary">
                                                {invoice.invoiceNumber}
                                            </TableCell>
                                            <TableCell>{invoice.orderNumber}</TableCell>
                                            <TableCell>
                                                <div>
                                                    <div className="font-medium">{invoice.user?.name || 'N/A'}</div>
                                                    <div className="text-xs text-muted-foreground">{invoice.user?.email}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-semibold">
                                                {formatCurrency(invoice.totalAmount)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={invoice.status === 'downloaded' ? 'success' : 'secondary'} className="capitalize">
                                                    {invoice.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {invoice.downloadCount > 0 ? (
                                                    <span className="text-sm">{invoice.downloadCount}x</span>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                                            <TableCell className="text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {invoice.pdfUrl && (
                                                            <DropdownMenuItem asChild>
                                                                <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
                                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                                    View PDF
                                                                </a>
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            onClick={() => handleRegenerate(invoice.orderId)}
                                                            disabled={regenerating === invoice.orderId}
                                                        >
                                                            <RefreshCw className={`h-4 w-4 mr-2 ${regenerating === invoice.orderId ? 'animate-spin' : ''}`} />
                                                            Regenerate
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {!loading && filteredInvoices.length > 0 && (
                        <div className="p-4 border-t">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={goToPage}
                                summary={`Page ${currentPage} of ${totalPages}`}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Config Modal */}
            <InvoiceConfigModal
                isOpen={configModalOpen}
                onClose={() => setConfigModalOpen(false)}
                onSaved={() => {
                    setConfigModalOpen(false);
                }}
            />
        </div>
    );
}
